import { useEffect, useState } from 'preact/hooks';
import type {
  CfoAction,
  CfoDecidedBody,
  CfoHistoryBody,
  CfoHistoryRow,
  CfoPageBody,
  CfoQueueBody,
  PolicyCheck,
} from '../../shared/po/approval';
import { PriceCompare } from '../components/PriceCompare';
import { personName } from '../lib/people';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/dates';

// CFO approval (screen 06 and its phone layout, P3.2). The list of POs waiting, and one PO with
// the internal note, lines, quotes, policy checks and trail. Approve issues the PO number; return
// and reject need a remark. The broker enforces every rule (preparer can't approve; D53).

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

type Load<T> =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: T };

function useLoad<T>(path: `/api/${string}`, reload: number): Load<T> {
  const [load, setLoad] = useState<Load<T>>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    apiGet<T>(path)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [path, reload]);
  return load;
}

function Problem({ code, reference }: { code: string; reference: string | null }) {
  const { t } = useI18n();
  return (
    <section class="card card--error" role="alert">
      <p>
        {code === 'forbidden'
          ? t('cfo.error.forbidden')
          : code === 'invalid_transition'
            ? t('cfo.error.notWaiting')
            : t('req.loadError')}
      </p>
      {reference && (
        <p class="caption" dir="ltr">
          {t('error.reference', { ref: reference })}
        </p>
      )}
    </section>
  );
}

/** Waiting for the CFO, and what the CFO already decided (D101). */
export function PoApprovalsPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState<'waiting' | 'decided'>('waiting');
  return (
    <div class="stack">
      <div class="admin-tabs" role="group" aria-label={t('cfo.tabs')}>
        <button
          type="button"
          class="chip"
          aria-pressed={tab === 'waiting'}
          onClick={() => setTab('waiting')}
        >
          {t('cfo.tab.waiting')}
        </button>
        <button
          type="button"
          class="chip"
          aria-pressed={tab === 'decided'}
          onClick={() => setTab('decided')}
        >
          {t('cfo.tab.decided')}
        </button>
      </div>
      {tab === 'waiting' ? <WaitingList /> : <DecidedList />}
    </div>
  );
}

const DECISION_PILL: Record<CfoHistoryRow['decision'], string> = {
  approve: 'pill--success',
  sign: 'pill--success',
  return: 'pill--neutral',
  reject: 'pill--danger',
};

function DecidedList() {
  const { t, lang } = useI18n();
  const load = useLoad<CfoHistoryBody>('/api/po-approvals/history', 0);
  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') return <Problem code={load.code} reference={load.reference} />;
  const { rows } = load.data;
  return (
    <section class="card" aria-labelledby="cfo-decided">
      <h2 id="cfo-decided" class="card__title">
        {t('cfo.decided.title', { n: String(rows.length) })}
      </h2>
      <p class="caption">{t('cfo.decided.lead')}</p>
      {rows.length === 0 ? (
        <p>{t('cfo.decided.none')}</p>
      ) : (
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">{t('cfo.decided.when')}</th>
                <th scope="col">{t('cfo.decided.ref')}</th>
                <th scope="col">{t('cfo.decided.supplier')}</th>
                <th scope="col" class="num">
                  {t('cfo.decided.total')}
                </th>
                <th scope="col">{t('cfo.decided.decision')}</th>
                <th scope="col">{t('cfo.decided.remark')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // After approval or signature the CFO can still open the request; after a
                // return or rejection it is back with Procurement and out of the CFO's view.
                const open = r.decision === 'approve' || r.decision === 'sign';
                return (
                  <tr key={`${r.when}-${r.prNo}`}>
                    <td>{formatDateTime(r.when, lang)}</td>
                    <td>
                      {open ? (
                        <a class="row-ref" href={`#/requests/${r.prNo}`} dir="ltr">
                          {r.poNo || r.prNo}
                        </a>
                      ) : (
                        <span class="row-ref" dir="ltr">
                          {r.poNo || r.prNo}
                        </span>
                      )}
                      {r.poNo && (
                        <span class="caption cell-sub" dir="ltr">
                          {r.prNo}
                        </span>
                      )}
                    </td>
                    <td dir="auto">{r.supplierName || '—'}</td>
                    <td class="num" dir="ltr">
                      {r.total ? `${r.currency} ${money(r.total)}` : '—'}
                    </td>
                    <td>
                      <span class={`pill ${DECISION_PILL[r.decision]}`}>
                        {t(`cfo.decided.${r.decision}` as MessageKey)}
                      </span>
                    </td>
                    <td dir="auto">{r.remark || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function WaitingList() {
  const { t, lang } = useI18n();
  const load = useLoad<CfoQueueBody>('/api/po-approvals', 0);
  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') return <Problem code={load.code} reference={load.reference} />;
  const { items } = load.data;
  return (
    <section class="card" aria-labelledby="cfo-list">
      <div class="card__head">
        <h2 id="cfo-list" class="card__title">
          {t('cfo.waiting', { n: String(items.length) })}
        </h2>
      </div>
      {items.length === 0 ? (
        <p>{t('cfo.none')}</p>
      ) : (
        <ul class="action-list cfo-list">
          {items.map((i) => (
            <li key={i.prNo}>
              <a class="cfo-row" href={`#/po-approvals/${i.prNo}`}>
                <span class="req-row__title">{i.title}</span>
                <span class="caption cell-sub">
                  <span dir="ltr">{i.poNo || i.prNo}</span> · {i.supplierName} · {i.vertical}
                  {i.submittedAt && ` · ${formatDate(i.submittedAt, lang)}`}
                </span>
                <span class="cfo-row__amount" dir="ltr">
                  {i.currency} {money(i.total)}
                </span>
                <span class="cfo-row__pills">
                  {i.sentFirst && (
                    <span class={`pill pill--${i.overdue ? 'danger' : 'warning'}`}>
                      {i.overdue ? t('cfo.overdue') : t('cfo.poStatus.sentPendingCFO')}
                      {i.signDue && ` · ${t('cfo.signDue', { date: formatDate(i.signDue, lang) })}`}
                    </span>
                  )}
                  {i.stage === 'vertical' && (
                    <span class="pill pill--info">{t('cfo.vertical.title')}</span>
                  )}
                  {i.rush && <span class="pill pill--warning">{t('req.rush')}</span>}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const ICON: Record<PolicyCheck['level'], string> = { ok: '✓', warn: '!', block: '✕' };

function CheckText({ c }: { c: PolicyCheck }) {
  const { t, lang } = useI18n();
  const v = { ...c.values };
  if (v.date) v.date = formatDate(v.date, lang);
  if (v.docs)
    v.docs = v.docs
      .split(',')
      .map((d) => t(`cfo.doc.${d}` as MessageKey))
      .join(', ');
  return (
    <>
      <span class="cfo-check__title">{t(`cfo.check.${c.id}.${c.level}` as MessageKey, v)}</span>
      {c.id === 'quotes' && v.reason && (
        <span class="caption" dir="auto">
          {v.reason}
        </span>
      )}
      {c.id === 'rush' && v.reason && (
        <span class="caption" dir="auto">
          {v.reason}
        </span>
      )}
      {c.id === 'price' && v.pct && (
        <span class="caption" dir="ltr">
          {t('cfo.pctVsLast', { pct: v.pct })}
        </span>
      )}
    </>
  );
}

export function PoApprovalPage({ prNo }: { prNo: string }) {
  const { t, lang } = useI18n();
  const [reload, setReload] = useState(0);
  const load = useLoad<CfoPageBody>(`/api/prs/${encodeURIComponent(prNo)}/po/approval`, reload);
  const [remark, setRemark] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; ref?: string } | null>(null);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') return <Problem code={load.code} reference={load.reference} />;
  const d = load.data;
  const cur = d.currency;
  const blocked = d.checks.some((c) => c.level === 'block');

  async function decide(action: CfoAction) {
    if ((action === 'return' || action === 'reject') && !remark.trim()) {
      setNote({ ok: false, text: t('cfo.remarkNeeded') });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const out = await apiSend<CfoDecidedBody>(
        'POST',
        `/api/prs/${encodeURIComponent(prNo)}/po/approval`,
        { action, etag: d.etag, remark },
      );
      setNote({
        ok: true,
        text:
          action === 'approve'
            ? t('cfo.done.approve', { no: out.poNo })
            : t(`cfo.done.${action}` as MessageKey),
      });
      setRemark('');
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      const known = [
        'preparer_cannot_approve',
        'reason_required',
        'conflict',
        'invalid_transition',
        'forbidden',
      ];
      setNote({
        ok: false,
        text: t(
          (known.includes(e?.code ?? '')
            ? `cfo.error.${e?.code ?? ''}`
            : 'cfo.error.generic') as MessageKey,
        ),
        ...(e?.correlationId ? { ref: e.correlationId } : {}),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/po-approvals">{t('nav.poApprovals')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>
      </p>
      <div class="detail-head">
        <span class={`pill pill--${d.canDecide ? 'warning' : 'info'}`}>
          {t(`cfo.poStatus.${d.poStatus || 'none'}` as MessageKey)}
        </span>
        {d.vertical && <span class="pill pill--info">{d.vertical}</span>}
        {d.poNo && (
          <a class="pill" dir="ltr" href={`#/po/${d.poNo}`}>
            {d.poNo}
          </a>
        )}
      </div>
      <div class="cfo-head">
        <div>
          <h2 class="detail-title" dir="auto">
            {d.title}
          </h2>
          <p class="caption">
            {d.supplier.name} · {t('cfo.preparedBy', { by: d.preparedBy })} ·{' '}
            {t('cfo.from', { name: d.requesterName })}
          </p>
        </div>
        <p class="cfo-total">
          <span class="caption">{t('cfo.totalInclVat')}</span>
          <strong dir="ltr">
            {cur} {money(d.totals.total)}
          </strong>
        </p>
      </div>
      {note && (
        <div class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          <p>{note.text}</p>
          {note.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: note.ref })}
            </p>
          )}
        </div>
      )}
      {d.stage === 'vertical' && d.cfoApprovedBy && (
        <p class="notice notice--info" dir="auto">
          <strong>
            {t('cfo.vertical.cfoApproved')}: {personName(d.cfoApprovedBy)}
          </strong>
          {d.canDecide && <> · {t('cfo.vertical.note', { vertical: d.vertical })}</>}
          {d.cfoRemark && <> · {d.cfoRemark}</>}
        </p>
      )}
      {d.stage === 'cfo' && d.cfoRemark && !d.canDecide && (
        <p class="notice notice--info" dir="auto">
          {t('cfo.yourRemark')}: {d.cfoRemark}
        </p>
      )}
      {d.verticalRemark && !d.canDecide && (
        <p class="notice notice--info" dir="auto">
          {t('cfo.vertical.title')}: {d.verticalRemark}
        </p>
      )}

      <div class="po-layout">
        <div class="stack">
          {d.internalNote && (
            <section class="card po-note" aria-labelledby="cfo-note">
              <h3 id="cfo-note" class="cfo-note__title">
                {t('cfo.internalNote')}
              </h3>
              <p dir="auto">{d.internalNote}</p>
              {d.submittedAt && (
                <p class="caption">
                  {d.preparedBy} · {formatDateTime(d.submittedAt, lang)}
                </p>
              )}
            </section>
          )}

          <section class="card" aria-labelledby="cfo-lines">
            <h3 id="cfo-lines" class="card__title">
              {t('cfo.lines')}
            </h3>
            <table class="table po-schedule cfo-lines">
              <thead>
                <tr>
                  <th scope="col">{t('cfo.item')}</th>
                  <th scope="col" class="num">
                    {t('pr.line.qty')}
                  </th>
                  <th scope="col" class="num">
                    {t('cfo.unit')}
                  </th>
                  <th scope="col" class="num">
                    {t('po.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l, i) => (
                  <tr key={i}>
                    <td dir="auto">
                      {l.description}
                      {l.type !== 'one-off' && (
                        <span class="caption cell-sub">
                          {t(`po.type.${l.type}` as MessageKey)}
                          {l.type === 'monthly' && ` × ${l.months} ${t('po.months')}`}
                        </span>
                      )}
                    </td>
                    <td class="num" dir="ltr">
                      {l.qty}
                    </td>
                    <td class="num" dir="ltr">
                      {money(l.unitPrice)}
                    </td>
                    <td class="num" dir="ltr">
                      {money(l.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl class="po-totals">
              {d.totals.monthly > 0 && (
                <div>
                  <dt>{t('po.monthly')}</dt>
                  <dd dir="ltr">{money(d.totals.monthly)}</dd>
                </div>
              )}
              <div>
                <dt>{t('po.subtotal')}</dt>
                <dd dir="ltr">{money(d.totals.subtotal)}</dd>
              </div>
              <div>
                <dt>{t('po.vat', { rate: String(d.totals.vatRate) })}</dt>
                <dd dir="ltr">{money(d.totals.vat)}</dd>
              </div>
              <div class="po-totals__grand">
                <dt>{t('po.grandTotal')}</dt>
                <dd dir="ltr">
                  {cur} {money(d.totals.total)}
                </dd>
              </div>
            </dl>
            <p class="caption cfo-facts">
              <span>
                <strong>{t('cfo.payment')}:</strong> {t(`po.term.${d.paymentTerm}` as MessageKey)}
              </span>
              {d.deliverTo && (
                <span>
                  <strong>{t('cfo.delivery')}:</strong> {d.deliverTo}
                  {d.neededBy && ` · ${t('rfq.by', { date: formatDate(d.neededBy, lang) })}`}
                </span>
              )}
              {d.quoteRef && (
                <span>
                  <strong>{t('cfo.quoteRef')}:</strong> <span dir="ltr">{d.quoteRef}</span>
                </span>
              )}
            </p>
            {d.paymentTerm === 'custom' && (
              <ul class="cfo-schedule">
                {d.schedule.map((s, i) => (
                  <li key={i} dir="auto">
                    {s.milestone === 'milestone'
                      ? s.label
                      : t(`po.milestone.${s.milestone}` as MessageKey)}{' '}
                    ·{' '}
                    <span dir="ltr">
                      {s.pct}% · {money(s.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <PriceCompare prNo={d.prNo} category={d.category} currency={d.currency} lines={d.lines} />

          {d.canSign && (
            <section class="card" aria-labelledby="cfo-sign">
              <h3 id="cfo-sign" class="visually-hidden">
                {t('cfo.sign')}
              </h3>
              <p class="notice notice--action">
                {t('cfo.sentFirst', { date: formatDate(d.signDue, lang) })}
              </p>
              <div class="field">
                <label for="cfo-sign-remark">{t('cfo.remarkOptional')}</label>
                <textarea
                  id="cfo-sign-remark"
                  rows={2}
                  dir="auto"
                  maxLength={2000}
                  value={remark}
                  onInput={(e) => setRemark((e.target as HTMLTextAreaElement).value)}
                />
              </div>
              <div class="actions cfo-actions">
                <button
                  type="button"
                  class="button button--primary"
                  disabled={busy || blocked}
                  onClick={() => void decide('sign')}
                >
                  {t('cfo.sign')}
                </button>
              </div>
              {blocked && <p class="field__error">{t('cfo.error.preparerSign')}</p>}
            </section>
          )}
          {d.canDecide && (
            <section class="card" aria-labelledby="cfo-decide">
              <h3 id="cfo-decide" class="visually-hidden">
                {t('cfo.decide')}
              </h3>
              <div class="field">
                <label for="cfo-remark">{t('cfo.remark')}</label>
                <textarea
                  id="cfo-remark"
                  rows={3}
                  dir="auto"
                  maxLength={2000}
                  value={remark}
                  onInput={(e) => setRemark((e.target as HTMLTextAreaElement).value)}
                />
              </div>
              <div class="actions cfo-actions">
                <button
                  type="button"
                  class="button button--danger"
                  disabled={busy}
                  onClick={() => void decide('reject')}
                >
                  {t('cfo.reject')}
                </button>
                <button
                  type="button"
                  class="button"
                  disabled={busy}
                  onClick={() => void decide('return')}
                >
                  {t('cfo.return')}
                </button>
                <button
                  type="button"
                  class="button button--primary"
                  disabled={busy || blocked}
                  onClick={() => void decide('approve')}
                >
                  {t('cfo.approve')}
                </button>
              </div>
              {blocked && <p class="field__error">{t('cfo.error.preparer_cannot_approve')}</p>}
            </section>
          )}
        </div>

        <aside class="stack">
          <section class="card" aria-labelledby="cfo-checks">
            <h3 id="cfo-checks" class="card__title">
              {t('cfo.checks')}
            </h3>
            <ul class="cfo-checks">
              {d.checks.map((c) => (
                <li key={c.id} class={`cfo-check cfo-check--${c.level}`}>
                  <span class="cfo-check__icon" aria-hidden="true">
                    {ICON[c.level]}
                  </span>
                  <span class="visually-hidden">{t(`cfo.level.${c.level}` as MessageKey)}: </span>
                  <span class="cfo-check__body">
                    <CheckText c={c} />
                  </span>
                </li>
              ))}
            </ul>
          </section>
          <section class="card" aria-labelledby="cfo-quotes">
            <h3 id="cfo-quotes" class="card__title">
              {t('quote.title')}
            </h3>
            <ul class="cfo-quotes">
              {d.quotes.map((q, i) => (
                <li key={i}>
                  <span>
                    {q.selected ? <strong>{q.name}</strong> : q.name}
                    {q.selected && ` · ${t('cfo.selected')}`}
                  </span>
                  <span dir="ltr" class={q.noResponse ? 'caption' : ''}>
                    {q.noResponse ? t('quote.declined') : `${q.currency} ${money(q.amount)}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          <section class="card" aria-labelledby="cfo-trail">
            <h3 id="cfo-trail" class="card__title">
              {t('cfo.trail')}
            </h3>
            <ol class="cfo-trail">
              {d.trail.map((s, i) => (
                <li key={i}>
                  <span class="caption" dir="ltr">
                    {formatDate(s.when, lang)}
                  </span>
                  <span>
                    {t(`decision.${s.decision}` as MessageKey)} · {s.by}
                    {s.remark && (
                      <span class="caption cell-sub" dir="auto">
                        {s.remark}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </div>
  );
}
